import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { cn } from '~/shadcn/lib/utils'

const THUMB_SIZE = 40
const HALF_THUMB = THUMB_SIZE / 2

// CustomRange is required because Radix's default range causes visual issues when the range and thumb have the same height and the thumb is rounded.
const CustomRange = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ style, ...props }, ref) => {
  const rightPercentStr = style?.right as string
  const rightPercent = parseFloat(rightPercentStr) || 0

  const dynamicOffset = HALF_THUMB - THUMB_SIZE * (rightPercent / 100)

  const newStyle: React.CSSProperties = {
    ...style,
    right: `calc(${rightPercentStr} + ${dynamicOffset}px)`,
  }

  return <span {...props} ref={ref} style={newStyle} />
})
CustomRange.displayName = 'CustomRange'

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root>) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min],
    [value, defaultValue, min]
  )

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        'relative flex w-full touch-none items-center cursor-pointer select-none data-disabled:opacity-50',
        className
      )}
      {...props}>
      <SliderPrimitive.Track
        data-slot="slider-track"
        className="bg-muted relative grow overflow-hidden rounded-full h-10 w-full">
        <SliderPrimitive.Range asChild>
          <CustomRange
            data-slot="slider-range"
            className="bg-primary absolute h-full"
          />
        </SliderPrimitive.Range>
      </SliderPrimitive.Track>

      {_values.map((_, index) => (
        <SliderPrimitive.Thumb
          key={index}
          data-slot="slider-thumb"
          className="block size-10 shrink-0 rounded-full bg-[#e5e5e5] shadow-sm transition-[color,box-shadow] focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
          style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
        />
      ))}
    </SliderPrimitive.Root>
  )
}

export { Slider }
